import { Heart, Plus } from '@phosphor-icons/react'
import { Icon, getTextColorByBrightness } from '@shopflo/ui'
import { ListCarousel } from '@shopflo/ui'
import React, { useState } from 'react'
import { customizeCartUIType } from 'src/lib/types/cartUI'

// Tab configuration
const tabConfig = [
  { value: 'best-sellers', title: 'Best Sellers' },
  { value: 'you-may-like', title: 'You May Like' }
]

// Hardcoded products array for Best Sellers
const bestSellersProducts = [
  {
    id: 1,
    name: 'Premium Wireless Headphones',
    currentPrice: 1999,
    originalPrice: 2499,
    discount: 20
  },
  {
    id: 2,
    name: 'Smart Watch Series 5',
    currentPrice: 3499,
    originalPrice: null,
    discount: 0
  },
  {
    id: 3,
    name: 'Bluetooth Speaker Pro',
    currentPrice: 849,
    originalPrice: 999,
    discount: 15
  }
]

// Hardcoded products array for You May Like
const youMayLikeProducts = [
  {
    id: 4,
    name: 'Portable Power Bank 20000mAh',
    currentPrice: 1299,
    originalPrice: 1599,
    discount: 19
  },
  {
    id: 5,
    name: 'Wireless Gaming Mouse',
    currentPrice: 2499,
    originalPrice: null,
    discount: 0
  },
  {
    id: 6,
    name: 'USB-C Hub 7-in-1',
    currentPrice: 1799,
    originalPrice: 2299,
    discount: 22
  }
]

// UpSellProductCard UI component (hardcoded, no logic)
const UpSellProductCard = ({
  product,
  isLast,
  upsellConfig,
  wishlistConfig
}: {
  product: typeof bestSellersProducts[0]
  isLast: boolean
  upsellConfig: customizeCartUIType['upsell']
  wishlistConfig?: {
    enabled: boolean
    primaryColor: string
  }
}) => {
  return (
    <div className='upsell-card relative flex h-fit w-[196px] snap-start flex-col gap-2 cursor-pointer shrink-0'>
      <div className='flex flex-row gap-3 flex-1'>
        <div className='relative h-[68px] w-[68px] border border-gray-light rounded-lg overflow-hidden bg-gray-200'>
          <div className='h-full w-full bg-gray-200 animate-pulse' />
          {product.discount > 0 && (
            <div
              className='jusitfy-center absolute -left-[3px] -top-[3px] flex flex-col rounded-br-xl rounded-tl-xl border border-carbon-lighter bg-yay-dark px-2 py-1 text-text-xxs-semibold text-white'
              style={{
                backgroundColor: upsellConfig.offerBadgeColor
              }}
            >
              <span>{product.discount}%</span>
              <span>OFF</span>
            </div>
          )}
        </div>
        <div className='flex-1 flex flex-col space-y-0'>
          <p
            className='mt-1 text-wrap line-clamp-2 text-text-xs-medium'
            style={{
              color: upsellConfig.textColor
            }}
          >
            {product.name}
          </p>
          <div className='flex flex-row items-center gap-1'>
            <span
              className='text-text-xs-semibold'
              style={{
                color: upsellConfig.textColor
              }}
            >
              ₹{product.currentPrice}
            </span>
            {product.originalPrice && (
              <span
                className='text-text-xxs-regular text-gray-dark line-through'
                style={{
                  color: upsellConfig.textColor,
                  opacity: 0.4
                }}
              >
                ₹{product.originalPrice}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className='flex flex-row w-full gap-2'>
        <div
          className='w-8 h-8 border rounded-md border-zinc-200 flex justify-center items-center'
          style={{
            borderColor: getTextColorByBrightness({
              bgHexColor: upsellConfig?.backgroundColor ?? '#FFFFFF',
              lightOpacity: 24
            })
          }}
        >
          <Icon
            IconComponent={Heart}
            size='md'
            iconProps={{
              className: 'h-4 w-4',
              weight: 'fill',
              color: wishlistConfig?.primaryColor
            }}
          />
        </div>
        <button
          className='flex flex-1 h-8 items-center justify-center space-x-1 rounded-lg bg-transparent border border-yay-dark px-3 text-text-sm-medium text-yay-dark'
          style={{
            borderColor: upsellConfig.buttonColor,
            color: upsellConfig.buttonColor
          }}
        >
          <Plus size={16} weight='bold' />
          <span className='text-text-xs-semibold'>ADD</span>
        </button>
      </div>
    </div>
  )
}

interface UpsellProps {
  upsellConfig: customizeCartUIType['upsell']
  wishlistConfig?: {
    enabled: boolean
    primaryColor: string
  }
}

export const Upsell: React.FC<UpsellProps> = ({ upsellConfig, wishlistConfig }) => {
  const [activeTab, setActiveTab] = useState('best-sellers')

  const currentProducts = activeTab === 'best-sellers' ? bestSellersProducts : youMayLikeProducts

  const borderStyles =
    upsellConfig.cardBorderSides === 'bottom'
      ? {
          borderBottomWidth: upsellConfig.cardBorderWidth,
          borderBottomColor: upsellConfig.borderColor,
          borderBottomStyle: 'solid' as const
        }
      : {
          borderWidth: upsellConfig.cardBorderWidth,
          borderColor: upsellConfig.borderColor,
          borderStyle: 'solid' as const
        }

  return (
    <div
      className='flex w-full flex-col gap-3 pt-3'
      style={{
        backgroundColor: upsellConfig.backgroundColor,
        borderRadius: upsellConfig.cardRadius,
        ...borderStyles
      }}
    >
      {/* Tab List */}
      <div className='mb-2 flex items-start space-x-4 pl-3'>
        {tabConfig.map(tab => {
          const isSelected = activeTab === tab.value
          return (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`relative text-text-xs-semibold`}
              style={{
                color: isSelected ? upsellConfig.titleColor : upsellConfig.textColor
              }}
            >
              {tab.title}
              {isSelected && (
                <span
                  className='absolute -bottom-2 left-0 w-full border-1'
                  style={{
                    borderColor: upsellConfig.titleColor
                  }}
                ></span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <ListCarousel count={currentProducts.length} customCSS='px-3' shouldRenderDots={false}>
        {currentProducts.map((product, index) => (
          <UpSellProductCard
            key={product.id}
            product={product}
            isLast={index === currentProducts.length - 1}
            upsellConfig={upsellConfig}
            wishlistConfig={wishlistConfig}
          />
        ))}
      </ListCarousel>
    </div>
  )
}
